import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useMediaQuery } from "@/hooks/use-mobile";
import { AeroSyncLogo } from "@/components/ui/aerosync-logo";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { 
  Home, 
  FileText, 
  AlertTriangle, 
  ShieldCheck, 
  Shield,
  CalendarCheck, 
  Settings,
  ChevronLeft,
  ChevronRight,
  User,
  Plane,
  LogOut,
  ListFilter,
  Tags,
  Building2,
  Receipt,
  DollarSign
} from "lucide-react";
import aerosyncLogo from "@/assets/images/aerosync_logo.jpg";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();
  const isMobile = useMediaQuery("(max-width: 768px)");
  
  // Automatically collapse sidebar on mobile
  useEffect(() => {
    if (isMobile && !collapsed) {
      onToggle();
    }
  }, [isMobile]);
  
  const handleLogout = () => {
    logoutMutation.mutate();
  };

  // Admin navigation items (Dashboard + Admin features)
  const adminNavItems = [
    {
      title: "Dashboard",
      href: "/",
      icon: <Home className="h-5 w-5" />,
    },
    {
      title: "Regulations",
      href: "/regulations",
      icon: <AlertTriangle className="h-5 w-5" />,
    },
    {
      title: "Categories",
      href: "/regulation-category",
      icon: <Tags className="h-5 w-5" />,
    },
    {
      title: "Authorities",
      href: "/authorities",
      icon: <Shield className="h-5 w-5" />,
    },
    {
      title: "Organizations",
      href: "/organizations",
      icon: <Building2 className="h-5 w-5" />,
    },
    {
      title: "Technical Profiles",
      href: "/admin-technical-profiles",
      icon: <Settings className="h-5 w-5" />,
    },
    /* Commented out compliance path
    {
      title: "Compliance",
      href: "/compliance",
      icon: <ShieldCheck className="h-5 w-5" />,
    },
    */
    {
      title: "Admin Users",
      href: "/admin-users",
      icon: <User className="h-5 w-5" />,
    },
  ];
  
  // Auditor navigation items (Dashboard + Auditor features)
  const auditorNavItems = [
    {
      title: "Dashboard",
      href: "/",
      icon: <Home className="h-5 w-5" />,
    },
    {
      title: "Manuals",
      href: "/manuals",
      icon: <FileText className="h-5 w-5" />,
    },
    /* Commented out compliance path
    {
      title: "Compliance",
      href: "/compliance",
      icon: <ShieldCheck className="h-5 w-5" />,
    },
    */
    {
      title: "Audits",
      href: "/audits",
      icon: <CalendarCheck className="h-5 w-5" />,
    },
    {
      title: "Billing",
      href: "/billing",
      icon: <Receipt className="h-5 w-5" />,
    },
  ];
  
  // Choose navigation items based on user role
  const navItems = user?.role === "admin" ? adminNavItems : auditorNavItems;

  const profileItems = [
    {
      title: "Profile",
      href: "/profile",
      icon: <User className="h-5 w-5" />,
    },
    {
      title: "Settings",
      href: "/profile?tab=settings",
      icon: <Settings className="h-5 w-5" />,
    },
  ];

  return (
    <div
      className={cn(
        "bg-[#01266A] dark:bg-[#001845] text-white flex-shrink-0 flex flex-col h-full transition-all duration-300 ease-in-out",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo and toggle */}
      <div className="p-4 flex items-center justify-between border-b border-gray-700/70 dark:border-gray-800">
        <div className="flex items-center space-x-2 sidebar-logo">
          {collapsed ? (
            <AeroSyncLogo width={32} height={32} />
          ) : (
            <div className="flex items-center gap-3">
              <AeroSyncLogo width={32} height={32} />
              <h1 className="text-xl font-bold text-white dark:text-white">AeroSync</h1>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="hover:bg-[#0072AB] hover:bg-opacity-20 dark:hover:bg-[#0072AB]/20 text-white"
        >
          {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </Button>
      </div>

      {/* Main navigation */}
      <nav className="mt-4 px-2 flex-1">
        <div className="space-y-1">
          {navItems.map((item) => (
            <Link 
              key={item.href} 
              href={item.href}
              className={cn(
                "flex items-center space-x-3 px-3 py-2 rounded-md transition-colors duration-200",
                location === item.href
                  ? "bg-[#0072AB] bg-opacity-20 dark:bg-[#0072AB]/40 text-white"
                  : "text-white hover:bg-[#0072AB] hover:bg-opacity-10 dark:hover:bg-[#0072AB]/20"
              )}
            >
              {item.icon}
              {!collapsed && <span>{item.title}</span>}
            </Link>
          ))}
        </div>

        {/* Profile section */}
        <div className="mt-8">
          {!collapsed && (
            <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Organization Profile
            </h3>
          )}
          <div className="mt-2 space-y-1">
            {profileItems.map((item) => (
              <Link 
                key={item.href} 
                href={item.href}
                className={cn(
                  "flex items-center space-x-3 px-3 py-2 rounded-md transition-colors duration-200",
                  location === item.href
                    ? "bg-[#0072AB] bg-opacity-20 dark:bg-[#0072AB]/40 text-white"
                    : "text-white hover:bg-[#0072AB] hover:bg-opacity-10 dark:hover:bg-[#0072AB]/20"
                )}
              >
                {item.icon}
                {!collapsed && <span>{item.title}</span>}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Theme toggle and logout */}
      <div className="mt-auto p-4 border-t border-gray-700/70 dark:border-gray-800">
        <div className="flex items-center justify-between mb-4">
          {!collapsed && <span className="text-xs text-gray-400">Theme</span>}
          <ThemeToggle variant="outline" size="sm" />
          {!collapsed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-2 text-white/80 hover:text-white hover:bg-[#0072AB]/20 dark:hover:bg-[#0072AB]/30"
              disabled={logoutMutation.isPending}
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </Button>
          )}
          {collapsed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-white/80 hover:text-white hover:bg-[#0072AB]/20 dark:hover:bg-[#0072AB]/30 mt-2"
              disabled={logoutMutation.isPending}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* User info */}
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 rounded-full bg-[#0072AB] dark:bg-blue-600 flex items-center justify-center text-white">
            {user?.fullName ? user.fullName.charAt(0).toUpperCase() : "U"}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate flex items-center gap-2">
                {user?.fullName || "User"}
                {user?.role === "admin" && (
                  <span className="px-1.5 py-0.5 text-xs rounded-full bg-[#0072AB] dark:bg-blue-600 text-white">Admin</span>
                )}
                {user?.role === "auditor" && (
                  <span className="px-1.5 py-0.5 text-xs rounded-full bg-[#01266A] dark:bg-blue-900 text-white">Auditor</span>
                )}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {user?.role === "admin" 
                  ? "AeroSync Employee" 
                  : user?.airlineName || "Organization Staff"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
